import React, { useState, useEffect } from 'react';
import TimeSeriesChart from '@/components/shared/TimeSeriesChart';
import { subHours } from 'date-fns';
import { apiService } from '@/utils/api';
import { useAnalytics } from './AnalyticsContext';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { ManualResponse } from '@/types/api';
import { MotorStatus } from '@/types/motorStatus';

interface TimeSeriesPoint {
  timestamp: string;
  value: number;
}

interface TimeSeriesData {
  metric: string;
  points: TimeSeriesPoint[];
}

// 主动采集写入 InfluxDB 的字段：aux<slot>（1=开 0=关）和 motor_status
const AUX_FIELD_REGEX = /^aux(\d+)$/;
const MOTOR_STATUS_FIELD = 'motor_status';
const MAIN_PUMP_FIELD = 'aux0';
const PUMP_TYPE_CODE = 31; // SPALSHME_PUMP

const SERIES_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
  '#ec4899', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7',
];

// motor_status 枚举（与 @/types/motorStatus 一致），y 轴逐一展示，每个值的文案唯一
const MOTOR_STATUS_LABELS: Record<number, string> = {
  [MotorStatus.MOTOR_STATUS_NOT_READY]: 'Not Ready',
  [MotorStatus.MOTOR_STATUS_DEFAULT_DATA_SEND]: 'Default Data Send',
  [MotorStatus.MOTOR_STATUS_READY]: 'Ready',
  [MotorStatus.MOTOR_STATUS_RUNNING]: 'Running',
  [MotorStatus.MOTOR_STATUS_STARTING]: 'Starting',
  [MotorStatus.MOTOR_STATUS_STOPPING]: 'Stopping',
  [MotorStatus.MOTOR_STATUS_STOPPED]: 'Stopped',
  [MotorStatus.MOTOR_STATUS_ERROR]: 'Error',
  [MotorStatus.MOTOR_STATUS_OVER_TEMP]: 'Over Temp',
  [MotorStatus.MOTOR_STATUS_IDLE]: 'Idle',
};
const MOTOR_STATUS_VALUES = Object.keys(MOTOR_STATUS_LABELS).map(Number).sort((a, b) => a - b);
const MOTOR_STATUS_MIN = MOTOR_STATUS_VALUES[0];
const MOTOR_STATUS_MAX = MOTOR_STATUS_VALUES[MOTOR_STATUS_VALUES.length - 1];

const formatMotorStatus = (value: number): string => {
  const label = MOTOR_STATUS_LABELS[value];
  return label ? `${label} (${value})` : `Unknown (${value})`;
};

const slotOf = (field: string): number => {
  const match = field.match(AUX_FIELD_REGEX);
  return match ? parseInt(match[1], 10) : -1;
};

const AuxStatusPage: React.FC = () => {
  const { deviceId } = useAnalytics();
  const { mode } = useSelector((state: RootState) => state.theme);

  const [endTimeState, setEndTimeState] = useState<Date>(new Date());
  const [startTimeState, setStartTimeState] = useState<Date>(subHours(new Date(), 24));
  const [interval, setInterval] = useState<string>('15m');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [auxFields, setAuxFields] = useState<string[]>([]);
  const [hasMotorStatus, setHasMotorStatus] = useState<boolean>(false);
  const [auxLabels, setAuxLabels] = useState<Record<number, string>>({});
  const [auxSeries, setAuxSeries] = useState<TimeSeriesData[]>([]);
  const [motorSeries, setMotorSeries] = useState<TimeSeriesData[]>([]);

  const formattedStartTime = startTimeState.toISOString();
  const formattedEndTime = endTimeState.toISOString();

  // 1. 设备切换时：获取该设备在 InfluxDB 中有哪些 aux 字段，以及 aux 名称（名称获取失败不影响展示）
  useEffect(() => {
    if (!deviceId) return;
    let mounted = true;

    setAuxFields([]);
    setHasMotorStatus(false);
    setAuxLabels({});

    apiService
      .get<{ metrics: string[] }>(`/api/v1/sensor-data/metrics?device_id=${encodeURIComponent(deviceId)}`)
      .then((resp) => {
        if (!mounted) return;
        const metrics = resp?.metrics || [];
        const fields = metrics
          .filter((m) => AUX_FIELD_REGEX.test(m))
          .sort((a, b) => slotOf(a) - slotOf(b));
        setAuxFields(fields);
        setHasMotorStatus(metrics.includes(MOTOR_STATUS_FIELD));
        if (fields.length === 0 && !metrics.includes(MOTOR_STATUS_FIELD)) {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching available metrics:', err);
        if (!mounted) return;
        setError('Failed to fetch available metrics');
        setLoading(false);
      });

    apiService
      .get<ManualResponse>(`/api/v1/iot-shadow/device/${deviceId}/manual`)
      .then((resp) => {
        if (!mounted) return;
        const labels: Record<number, string> = {};
        (resp?.manual?.aux || []).forEach((aux) => {
          if (aux.aux_type_code === PUMP_TYPE_CODE) {
            labels[aux.aux_slot_num] = aux.aux_tag || 'Main Pump';
          } else if (aux.aux_tag) {
            labels[aux.aux_slot_num] = aux.aux_tag;
          }
        });
        setAuxLabels(labels);
      })
      .catch((err) => {
        console.warn('Failed to fetch aux names, fallback to slot names:', err);
      });

    return () => {
      mounted = false;
    };
  }, [deviceId]);

  // 2. 拉取时间序列（按 interval 聚合，均值即为开启占比）以及最新的原始状态
  useEffect(() => {
    if (!deviceId) return;
    if (auxFields.length === 0 && !hasMotorStatus) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const baseParams = {
          device_id: deviceId,
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          interval,
        };
        const [auxResp, motorResp] = await Promise.all([
          auxFields.length > 0
            ? apiService.post<TimeSeriesData[]>('/api/v1/sensor-data/time-series', { ...baseParams, metrics: auxFields })
            : Promise.resolve([] as TimeSeriesData[]),
          // 枚举值不能取平均，按区间取最后一个值
          hasMotorStatus
            ? apiService.post<TimeSeriesData[]>('/api/v1/sensor-data/time-series', {
                ...baseParams,
                metrics: [MOTOR_STATUS_FIELD],
                aggregate: 'last',
              })
            : Promise.resolve([] as TimeSeriesData[]),
        ]);

        const aggregatedData = auxResp || [];
        // 区间内取均值后四舍五入为 0/1：区间内大部分时间开着即为 1
        setAuxSeries(
          aggregatedData
            .filter((d) => AUX_FIELD_REGEX.test(d.metric) && d.points.length > 0)
            .map((d) => ({
              metric: d.metric,
              points: d.points.map((p) => ({ timestamp: p.timestamp, value: Math.round(p.value) })),
            }))
        );
        setMotorSeries((motorResp || []).filter((d) => d.metric === MOTOR_STATUS_FIELD && d.points.length > 0));

      } catch (err) {
        console.error('Error fetching aux status data:', err);
        setError('Failed to fetch aux status data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [deviceId, auxFields, hasMotorStatus, formattedStartTime, formattedEndTime, interval]);

  const getAuxLabel = (field: string): string => {
    const slot = slotOf(field);
    if (auxLabels[slot]) return auxLabels[slot];
    if (field === MAIN_PUMP_FIELD) return 'Main Pump';
    return `AUX ${slot}`;
  };

  const chartAuxKeys = auxSeries.map((d) => d.metric);

  const handleIntervalChange = (newInterval: string) => {
    setInterval(newInterval);
  };

  const handleDateRangeChange = (newStartTime: string, newEndTime: string) => {
    setStartTimeState(new Date(newStartTime));
    setEndTimeState(new Date(newEndTime));
  };

  const renderLoading = () => (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border text-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 h-full">
        <div className="lg:flex items-center justify-between mb-4 gap-3">
          <div className="mb-4 lg:mb-0">
            <h3 className={classNames(
              "text-lg font-semibold",
              {
                'text-gray-900': mode === 'light',
                'text-gray-200': mode === 'dark'
              }
            )}>Aux Status</h3>
            <p className={classNames(
              "mt-1 text-sm",
              {
                'text-gray-500': mode === 'light',
                'text-gray-400': mode === 'dark'
              }
            )}>History of aux on/off state and pump motor status</p>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {chartAuxKeys.length > 0 ? (
            <div className="card card-border">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4>Aux On/Off Over Time</h4>
                    <p>On/off state of each aux over time (1 = On, 0 = Off)</p>
                  </div>
                </div>

                {loading && renderLoading()}

                {!loading && !error && (
                  <TimeSeriesChart
                    deviceId={deviceId}
                    startTime={formattedStartTime}
                    endTime={formattedEndTime}
                    interval={interval}
                    height={500}
                    data={auxSeries}
                    showTitle={false}
                    onIntervalChange={handleIntervalChange}
                    onDateRangeChange={handleDateRangeChange}
                    metricKeys={chartAuxKeys}
                    seriesNames={chartAuxKeys.map(getAuxLabel)}
                    chartColors={chartAuxKeys.map((_, idx) => SERIES_COLORS[idx % SERIES_COLORS.length])}
                    yAxisTitle="State (1 = On, 0 = Off)"
                    curve="stepline"
                    yMin={0}
                    yMax={1}
                    yTickAmount={1}
                    title="Aux Status"
                    subtitle="Monitor aux on/off state over time"
                  />
                )}
              </div>
            </div>
          ) : (
            !loading && (
              <div className="flex justify-center items-center h-64">
                <p>No data available</p>
              </div>
            )
          )}

          {motorSeries.length > 0 && (
            <div className="card card-border">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4>Pump Motor Status Over Time</h4>
                    <p>Motor status reported by the pump (last value within every interval)</p>
                  </div>
                </div>

                {loading && renderLoading()}

                {!loading && !error && (
                  <TimeSeriesChart
                    deviceId={deviceId}
                    startTime={formattedStartTime}
                    endTime={formattedEndTime}
                    interval={interval}
                    height={520}
                    data={motorSeries}
                    showTitle={false}
                    onIntervalChange={handleIntervalChange}
                    onDateRangeChange={handleDateRangeChange}
                    metricKey={MOTOR_STATUS_FIELD}
                    yAxisTitle=""
                    seriesName="Motor Status"
                    chartColor="#8b5cf6"
                    curve="stepline"
                    yMin={MOTOR_STATUS_MIN}
                    yMax={MOTOR_STATUS_MAX}
                    yTickAmount={MOTOR_STATUS_MAX - MOTOR_STATUS_MIN}
                    valueFormatter={formatMotorStatus}
                    yLabelMaxWidth={220}
                    title="Motor Status"
                    subtitle="Monitor pump motor status over time"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuxStatusPage;
